import { useMemo, useState } from 'react';
import { Download, MapPinned, Network, Store } from 'lucide-react';
import type { EChartsCoreOption } from 'echarts/core';
import Chart from '../components/Chart';
import { categoryTotals, categoryTrend, terminalRows } from '../lib/analytics';
import { exportTerminals } from '../lib/exporter';
import { formatCompactCurrency, formatNumber, formatPercent } from '../lib/format';
import type { SalesRecord } from '../types';

type DimensionKey = 'flowType' | 'customerType' | 'province';

interface TerminalViewProps {
  records: SalesRecord[];
  targetProduct: string;
  dateLabel: string;
}

const dimensionLabels: Record<DimensionKey, string> = {
  flowType: '流向大类',
  customerType: '终端类型',
  province: '省份',
};

const palette = ['#2563eb', '#18a394', '#7868e6', '#f59e0b', '#ee6f72', '#22a5d8', '#8090aa'];
const axisText = { color: '#7d899d', fontFamily: 'Inter, Microsoft YaHei, sans-serif' };

export default function TerminalView({ records, targetProduct, dateLabel }: TerminalViewProps) {
  const [dimension, setDimension] = useState<DimensionKey>('flowType');
  const totals = useMemo(() => categoryTotals(records, dimension), [dimension, records]);
  const trend = useMemo(() => categoryTrend(records, dimension), [dimension, records]);
  const terminals = useMemo(() => terminalRows(records, targetProduct), [records, targetProduct]);

  const trendOption = useMemo<EChartsCoreOption>(() => ({
    color: palette,
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' }, valueFormatter: (value: unknown) => formatCompactCurrency(Number(value)) },
    legend: { top: 0, right: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: axisText },
    grid: { left: 18, right: 18, top: 48, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: trend.points.map((item) => item.label), axisTick: { show: false }, axisLine: { lineStyle: { color: '#e8edf5' } }, axisLabel: axisText },
    yAxis: { type: 'value', axisLabel: { ...axisText, formatter: (value: number) => `${Math.round(value / 10000)}万` }, splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } } },
    series: trend.categories.map((category) => ({
      name: category,
      type: 'bar',
      stack: 'sales',
      barMaxWidth: 28,
      emphasis: { focus: 'series' },
      data: trend.points.map((point) => Number(point[category] ?? 0)),
    })),
  }), [trend]);

  const compositionOption = useMemo<EChartsCoreOption>(() => ({
    color: palette,
    tooltip: { trigger: 'item', backgroundColor: '#172033', borderWidth: 0, textStyle: { color: '#fff' }, formatter: (params: { name: string; value: number; percent: number }) => `${params.name}<br/>${formatCompactCurrency(params.value)} · ${params.percent.toFixed(1)}%` },
    legend: { bottom: 0, left: 'center', icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: axisText },
    series: [{
      type: 'pie', radius: ['51%', '75%'], center: ['50%', '43%'], padAngle: 3,
      itemStyle: { borderRadius: 7 }, label: { show: false },
      data: totals.map((item) => ({ name: item.name, value: Number(item.sales.toFixed(2)) })),
    }],
  }), [totals]);

  return (
    <div className="view-stack">
      <section className="section-heading heading-row standalone-heading">
        <div>
          <span className="eyebrow">终端分析</span>
          <h2>看清大类结构与趋势变化</h2>
          <p>可按流向大类、终端类型或省份切换，所有图表与排行同步更新。</p>
        </div>
        <div className="heading-actions">
          <div className="segment-control wide-segments">
            {(Object.keys(dimensionLabels) as DimensionKey[]).map((key) => <button key={key} type="button" className={dimension === key ? 'active' : ''} onClick={() => setDimension(key)}>{dimensionLabels[key]}</button>)}
          </div>
          <button className="secondary-button" type="button" onClick={() => void exportTerminals(terminals, dateLabel)}><Download size={16} /> 下载终端数据</button>
        </div>
      </section>

      <section className="mini-metric-grid category-cards">
        {totals.slice(0, 4).map((item, index) => (
          <article className="mini-metric" key={item.name}>
            <span className={`mini-icon ${['blue', 'teal', 'violet', 'amber'][index]}`}>
              {dimension === 'province' ? <MapPinned size={18} /> : dimension === 'flowType' ? <Network size={18} /> : <Store size={18} />}
            </span>
            <div><span>{item.name}</span><strong>{formatCompactCurrency(item.sales)}</strong><small>{item.terminals} 家终端 · {formatPercent(item.share)}</small></div>
          </article>
        ))}
      </section>

      <section className="dashboard-grid dashboard-grid-wide">
        <article className="panel panel-wide">
          <div className="panel-head"><div><span className="eyebrow">趋势拆解</span><h3>{dimensionLabels[dimension]}销售趋势</h3></div><span className="panel-badge">堆叠净额</span></div>
          <Chart option={trendOption} className="chart-tall" ariaLabel={`${dimensionLabels[dimension]}销售额堆叠趋势图`} />
        </article>
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">结构占比</span><h3>{dimensionLabels[dimension]}贡献</h3></div></div>
          <Chart option={compositionOption} className="chart-tall" ariaLabel={`${dimensionLabels[dimension]}销售额占比环形图`} />
        </article>
      </section>

      <section className="panel table-panel">
        <div className="panel-head"><div><span className="eyebrow">终端排行</span><h3>终端贡献与单品覆盖</h3></div><span className="panel-badge">共 {formatNumber(terminals.length)} 家</span></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>#</th><th>终端名称</th><th>区域</th><th>流向类型</th><th>销售额</th><th>数量</th><th>覆盖产品</th><th>重点单品</th><th>最近采购</th></tr></thead>
            <tbody>
              {terminals.map((row, index) => (
                <tr key={row.customerCode}>
                  <td className="rank-cell">{String(index + 1).padStart(2, '0')}</td>
                  <td><strong className="cell-strong">{row.customerName}</strong><small className="cell-sub">{row.customerCode}</small></td>
                  <td>{row.province} · {row.city}</td><td>{row.flowType}</td><td>{formatCompactCurrency(row.sales)}</td><td>{formatNumber(row.quantity)}</td><td>{row.productCount} 个</td>
                  <td><span className={`status-pill ${row.hasTargetProduct ? 'good' : 'attention'}`}>{row.hasTargetProduct ? '已渗透' : '待开发'}</span></td><td>{row.lastPurchase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
